import { useParams, useRouteContext } from "@tanstack/react-router";
import { User, UserOrg } from "@/models/user.tsx";
import { useUserDetail } from "@/queries.ts";
import LayoutContentArea from "@/components/layout/LayoutContentArea";
import Loader from "@/components/shared/Loader";
import Breadcrumb from "@/components/shared/BreadCrumb";
import { LuUser } from "react-icons/lu";
import { Card } from "react-bootstrap";
import { UserProfileForm } from "@/components/user-management/components/UserProfileForm";
import { UserOrganizationsInline } from "@/components/user-management/components/UserOrganizationsInline";

export default function UserAdminPage() {
  const leaContext = useRouteContext({ from: "/$leaid" });

  const params = useParams({ strict: false });
  const result = useUserDetail(
    leaContext.currentUserOrg.esiIdPath,
    params.userid ?? ""
  );

  if (leaContext.hasRole("ADMIN") !== true) return null;
  if (result.isPending) return <Loader containerClass="py-5" />;
  if (result.error)
    return <div>An error has occurred: {result.error.message}</div>;

  const user = {
    ...result.data,
    userName: result.data.userName ?? params.userid ?? "",
  };

  const fullName =
    `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() ||
    user.userName ||
    "New User";

  const breadcrumbItems = [
    {
      label: "Home",
      to: "/$leaid" as const,
      params: { leaid: params.leaid ?? "" },
      ariaLabel: "Go to Home",
      isHome: true,
    },
    {
      label: "User Management",
      to: "/$leaid/admin/users" as const,
      params: { leaid: params.leaid ?? "" },
    },
    { label: fullName },
  ];

  return (
    <LayoutContentArea
      headingText={null}
      noBorder={true}
      showHeadingSection={false}
    >
      <Breadcrumb items={breadcrumbItems} />
      <div style={{ marginBottom: "1.5rem" }} />

      <Card className="no-padding card-border mb-5 overflow-hidden shadow">
        <div
          className="d-flex align-items-center gap-2 px-4 py-3 fw-semibold"
          style={{
            background: "var(--color-p3)",
            color: "#fff",
            fontSize: "0.975rem",
          }}
        >
          <LuUser size={16} style={{ flexShrink: 0 }} />
          Personal Information
        </div>
        <UserProfileForm user={user} />
      </Card>

      {user.userName && (
        <UserOrganizationsInline
          user={user}
          isAdmin={leaContext.hasRole("ADMIN") === true}
        />
      )}
    </LayoutContentArea>
  );
}

export function UserAdminData({
  user,
  hideActions,
  onSaveSuccess,
  onRegisterSubmit,
  onDirtyChange,
  onSubmittingChange,
}: {
  user: User<UserOrg>;
  hideActions?: boolean;
  onSaveSuccess?: (userName: string) => void;
  onRegisterSubmit?: (submitFn: () => void) => void;
  onDirtyChange?: (dirty: boolean) => void;
  onSubmittingChange?: (submitting: boolean) => void;
}) {
  return (
    <UserProfileForm
      user={user}
      hideActions={hideActions}
      onSaveSuccess={onSaveSuccess}
      onRegisterSubmit={onRegisterSubmit}
      onDirtyChange={onDirtyChange}
      onSubmittingChange={onSubmittingChange}
    />
  );
}
